import type { ChartClickData } from "../types/chart"
import { GROUP_BY_TYPE, type GroupByType } from "../types/chart-types"
import {
  AGGREGATION_TYPE,
  type AggregationType,
  type DataSource,
  type FilterType,
  type MetricFilter,
  type MetricOutputType,
} from "../types/core"
import { parseMetadataGroupKey } from "./chart/splitKeys"
import type { TableRowData } from "./tableWidgetData"

const GROUP_BY_ROW_ID_KEY: Partial<Record<GroupByType, keyof TableRowData>> = {
  [GROUP_BY_TYPE.AGENT]: "run_agent_id",
  [GROUP_BY_TYPE.MUTATION]: "mutation_id",
  [GROUP_BY_TYPE.PERSONA]: "persona_id",
  [GROUP_BY_TYPE.TEMPLATE]: "run_template_name",
  [GROUP_BY_TYPE.TEST_SET]: "test_set_id",
}

type TableCellGroupScope = Pick<
  ChartClickData,
  | "groupByMetadataKey"
  | "metadataGroupKind"
  | "metadataTopValues"
  | "splitBy"
  | "splitValue"
>

/**
 * Resolves the drill-down scope for one table row, or null when the row's
 * group cannot be expressed as a drill-down filter (assignee rows).
 */
const resolveTableCellGroupScope = ({
  groupBy,
  groupByMetadataKey,
  row,
}: {
  groupBy?: GroupByType
  groupByMetadataKey?: string
  row: TableRowData
}): TableCellGroupScope | null => {
  if (groupByMetadataKey) {
    const metadataGroup = row.metadataValue
      ? parseMetadataGroupKey(row.metadataValue)
      : null
    if (metadataGroup?.value == null) {
      return null
    }

    return {
      groupByMetadataKey,
      metadataGroupKind: metadataGroup.kind,
      ...(metadataGroup.topValues
        ? { metadataTopValues: metadataGroup.topValues }
        : {}),
      splitValue: metadataGroup.value,
    }
  }

  if (!groupBy) {
    return {}
  }

  const rowIdKey = GROUP_BY_ROW_ID_KEY[groupBy]
  const rowId = rowIdKey ? row[rowIdKey] : undefined
  if (typeof rowId !== "string" || rowId.length === 0) {
    return null
  }

  return { splitBy: groupBy, splitValue: rowId }
}

/**
 * Builds the drill-down request for one metric cell of a table widget: the
 * cell's metric, restricted to the row's group, the widget's filters, and the
 * table's date range.
 */
export const buildTableCellDrillDownClickData = ({
  aggregation,
  dataSource,
  dateRange,
  filters,
  groupBy,
  groupByMetadataKey,
  metricFilter,
  metricId,
  metricLabel,
  metricOutputType,
  row,
}: {
  aggregation?: AggregationType
  dataSource: DataSource
  dateRange: { start: Date; end: Date }
  filters?: FilterType
  groupBy?: GroupByType
  groupByMetadataKey?: string
  metricFilter?: MetricFilter[]
  metricId: string
  metricLabel: string
  metricOutputType: MetricOutputType
  row: TableRowData
}): ChartClickData | null => {
  const cellValue = row.metricCells[metricId]?.value
  if (cellValue == null) {
    return null
  }

  const groupScope = resolveTableCellGroupScope({
    groupBy,
    groupByMetadataKey,
    row,
  })
  if (!groupScope) {
    return null
  }

  return {
    drillDownScope: "point",
    timestamp: dateRange.start.getTime(),
    bucketTime: dateRange.start.toISOString(),
    endDate: dateRange.end.toISOString(),
    metricId,
    dataSource,
    aggregation: aggregation ?? AGGREGATION_TYPE.SUCCESS,
    metricOutputType,
    ...groupScope,
    filters: { ...filters, metricIds: [metricId] },
    ...(metricFilter ? { metricFilter } : {}),
    aggregatedValue: cellValue,
    displayLabel: metricLabel,
  }
}
